// Direct-play target resolution (presentation-2 contract section 6.1). Pure: given the choices,
// the table model and the package's target rules, it says which table anchors offer which
// choices. It never filters or renames a choice; anything it cannot place stays in the drawer.

import type { ChoiceView } from '../model/choices.ts';
import type { TableModel } from '../model/table.ts';
import type { Scalar } from '../protocol/ui.ts';
import type { TargetRule } from '../presentation/types.ts';

export interface TargetEntry {
  choiceId: string;
  label: string;
  group: string;
  /** Pre-answers the choice's first `pick` step when this anchor is an option object. */
  answer?: Scalar;
  /** That option's own label, which tells apart several options on one object (the payment a play uses). */
  detail?: string;
  /** Labelled button text from a target rule. */
  button?: string;
  /** The rule also offers this choice in the idle action bar. */
  bar?: boolean;
  attention?: boolean;
  via: 'subject' | 'option' | 'rule';
}

export interface TargetMap {
  /** Anchor key to the choices it offers, in choice order. */
  anchors: Map<string, TargetEntry[]>;
  /** Choice ID to the anchor keys that offer it. */
  byChoice: Map<string, string[]>;
  /** Choices with no table target; only the drawer offers them. */
  unresolved: ChoiceView[];
}

/** Anchor key for an object drawn by any brick. */
export const objectAnchor = (id: string) => `obj:${id}`;

/**
 * Resolves a target-rule anchor (`<type>:<selector>`) to an anchor key, or explains why not.
 * Types: `pile`, `zone`, `pool` take `zone:ID` or `counter:ID`; `slot` takes `zone:ID#N`; `mat` takes
 * a seat ID or `{self}`; `counter` takes `counter:ID`; `tile` takes a tile ID from an `action-tiles` brick.
 */
export function anchorKey(anchor: string, table: Pick<TableModel, 'zones' | 'counters' | 'seats' | 'selfSeat'>): string | { error: string } {
  const colon = anchor.indexOf(':');
  if (colon < 1) return { error: `Anchor "${anchor}" needs the form <type>:<selector>.` };
  const type = anchor.slice(0, colon);
  const selector = anchor.slice(colon + 1).replaceAll('{self}', table.selfSeat ?? '\u0000');
  const zone = (text: string) => (text.startsWith('zone:') ? table.zones.find(z => z.id === text.slice(5)) ?? null : null);
  const counter = (text: string) => (text.startsWith('counter:') ? table.counters.find(c => c.id === text.slice(8)) ?? null : null);
  switch (type) {
    case 'pile': case 'zone': case 'pool': {
      const z = zone(selector);
      if (z) return `${type}:${z.id}`;
      const c = type !== 'zone' ? counter(selector) : null;
      if (c) return `${type}:counter:${c.id}`;
      return { error: `"${selector}" names no zone${type !== 'zone' ? ' or counter' : ''} in this observation.` };
    }
    case 'slot': {
      const hash = selector.lastIndexOf('#');
      const z = hash > 0 ? zone(selector.slice(0, hash)) : null;
      const n = Number(selector.slice(hash + 1));
      return z && Number.isInteger(n) && n >= 0 ? `slot:${z.id}#${n}` : { error: `"${selector}" is not zone:ID#N for a zone in this observation.` };
    }
    case 'mat': {
      const seat = table.seats.find(s => s.id === selector);
      return seat ? `mat:${seat.id}` : { error: `"${selector}" names no seat.` };
    }
    case 'counter': {
      const c = counter(selector);
      return c ? `counter:${c.id}` : { error: `"${selector}" names no counter in this observation.` };
    }
    case 'tile':
      // A tile an `action-tiles` brick declares by ID (the validator checks the scene declares it).
      return /^[a-z][a-z0-9-]{0,31}$/.test(selector) ? `tile:${selector}` : { error: `"${selector}" is not a tile ID.` };
    default: return { error: `Unknown anchor type "${type}".` };
  }
}

function ruleMatches(rule: TargetRule, choice: ChoiceView): boolean {
  const m = rule.match;
  if (m.kind !== undefined && m.kind !== choice.action.kind) return false;
  if (m.group !== undefined && m.group !== choice.group) return false;
  if (m.data) for (const [k, v] of Object.entries(m.data)) if (choice.action.data[k] !== v) return false;
  return true;
}

/** The first step's option objects, when every option of a first `pick` step names one. */
export function firstStepObjects(choice: ChoiceView): { objectId: string; value: Scalar; label: string }[] | null {
  const step = choice.steps[0];
  if (!step || step.type !== 'pick' || step.expand || step.options.length === 0) return null;
  if (!step.options.every(o => !!o.objectId)) return null;
  return step.options.map(o => ({ objectId: o.objectId!, value: o.value, label: o.label }));
}

export function resolveTargets(choices: ChoiceView[], table: Pick<TableModel, 'zones' | 'counters' | 'seats' | 'selfSeat' | 'objects'>, rules: TargetRule[]): TargetMap {
  const anchors = new Map<string, TargetEntry[]>();
  const byChoice = new Map<string, string[]>();
  const unresolved: ChoiceView[] = [];
  const add = (key: string, entry: TargetEntry) => {
    const list = anchors.get(key) ?? [];
    if (list.some(e => e.choiceId === entry.choiceId && e.answer === entry.answer)) return;
    list.push(entry);
    anchors.set(key, list);
    const keys = byChoice.get(entry.choiceId) ?? [];
    if (!keys.includes(key)) keys.push(key);
    byChoice.set(entry.choiceId, keys);
  };
  for (const choice of choices) {
    const base = { choiceId: choice.id, label: choice.label, group: choice.group };
    let placed = false;
    // 1. Subjects.
    for (const id of choice.subjects) if (table.objects.has(id)) { add(objectAnchor(id), { ...base, via: 'subject' }); placed = true; }
    // 1b. A `priority` rule routes a subject-less choice before its option objects (a Rest door whose
    //     step then picks the card to destroy).
    if (!placed) {
      const rule = rules.find(r => !r.also && r.priority && ruleMatches(r, choice));
      const key = rule ? anchorKey(rule.anchor, table) : null;
      if (rule && typeof key === 'string') { add(key, { ...base, ...(rule.button ? { button: rule.button } : {}), ...(rule.bar ? { bar: true } : {}), ...(rule.attention ? { attention: true } : {}), via: 'rule' }); placed = true; }
    }
    // 2. First-step option objects, pre-answered; only when the choice has no subject on the table,
    //    so a gray route's color pick does not turn every hand stack into a claim target.
    if (!placed) for (const o of firstStepObjects(choice) ?? []) if (table.objects.has(o.objectId)) { add(objectAnchor(o.objectId), { ...base, answer: o.value, detail: o.label, via: 'option' }); placed = true; }
    // 3. Target rules, only for choices with neither.
    if (!placed) {
      const rule = rules.find(r => !r.also && ruleMatches(r, choice));
      const key = rule ? anchorKey(rule.anchor, table) : null;
      if (rule && typeof key === 'string') { add(key, { ...base, ...(rule.button ? { button: rule.button } : {}), ...(rule.bar ? { bar: true } : {}), ...(rule.attention ? { attention: true } : {}), via: 'rule' }); placed = true; }
    }
    // Optional secondary actions (for example a Move tile beside the pawn): the
    // same filtered choice retains its primary anchors and exact command/steps.
    for (const rule of rules.filter(r => r.also && ruleMatches(r, choice))) {
      const key = anchorKey(rule.anchor, table);
      if (typeof key === 'string') { add(key, { ...base, ...(rule.button ? { button: rule.button } : {}), ...(rule.bar ? { bar: true } : {}), ...(rule.attention ? { attention: true } : {}), via: 'rule' }); placed = true; }
    }
    // 4. Otherwise only the drawer offers it.
    if (!placed) unresolved.push(choice);
  }
  return { anchors, byChoice, unresolved };
}

/** Share of choices with at least one table target (1 when there are none). */
export function coverage(map: TargetMap, total: number): number {
  return total === 0 ? 1 : (total - map.unresolved.length) / total;
}

/**
 * Whether a choice's first pick is drawn as faces in a zone the viewer alone sees and that is not their hand (the cards a search or a
 * look at the deck offers): the cards are on screen, so a list of their names says nothing more.
 */
export function pickShowsFaces(choice: ChoiceView, table: Pick<TableModel, 'zones' | 'objects' | 'selfSeat'>): boolean {
  const objects = firstStepObjects(choice);
  if (!objects || !table.selfSeat) return false;
  return objects.every(o => {
    const object = table.objects.get(o.objectId);
    const zone = object ? table.zones.find(z => z.id === object.zoneId) : undefined;
    return !!zone && zone.visibility === 'owner' && zone.owner === table.selfSeat && zone.kind !== 'hand';
  });
}
